/**
 * React Query hooks for invoice management
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { invoicesApi } from '../services/api/invoices';
import type { 
  InvoiceCreate, 
  InvoiceUpdate, 
  InvoiceFilter,
  InvoicePaginationParams
} from '../types/invoice';
import { useToast } from './useToast';

/**
 * Hook to list invoices with filters and pagination
 */
export const useInvoices = (
  filters?: InvoiceFilter,
  pagination?: InvoicePaginationParams
) => {
  return useQuery(
    ['invoices', filters, pagination],
    () => invoicesApi.list(filters, pagination),
    {
      keepPreviousData: true,
    }
  );
};

/**
 * Hook to get a single invoice
 */
export const useInvoice = (invoiceId: string) => {
  return useQuery(
    ['invoices', invoiceId],
    () => invoicesApi.getById(invoiceId),
    {
      enabled: !!invoiceId,
    }
  );
};

/**
 * Hook to create an invoice
 */
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    (data: InvoiceCreate) => invoicesApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices']);
        showSuccess('Factura creada exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al crear factura');
      },
    }
  );
};

/**
 * Hook to update an invoice
 */
export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    ({ invoiceId, data }: { invoiceId: string; data: InvoiceUpdate }) =>
      invoicesApi.update(invoiceId, data),
    {
      onSuccess: (updatedInvoice) => {
        queryClient.invalidateQueries(['invoices']);
        queryClient.setQueryData(['invoices', updatedInvoice.invoiceId], updatedInvoice);
        showSuccess('Factura actualizada exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al actualizar factura');
      },
    }
  );
};

/**
 * Hook to delete an invoice
 */
export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    (invoiceId: string) => invoicesApi.delete(invoiceId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices']);
        showSuccess('Factura eliminada exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al eliminar factura');
      },
    }
  );
};

/**
 * Hook to get project invoice summary
 */
export const useProjectInvoiceSummary = (projectId: string) => {
  return useQuery(
    ['invoices', 'summary', projectId],
    () => invoicesApi.getProjectSummary(projectId),
    {
      enabled: !!projectId,
    }
  );
};
